import { Module } from 'vuex'
import firebase from 'firebase/app'
import { Distributor } from '@/types/Distributor'
import { RootState } from '@/store'

export interface DistributorsState {
  distributors: Distributor[]
  unsubscribeFromDistributors: () => void
}

const distributorsModule: Module<DistributorsState, RootState> = {
  namespaced: true,

  state: {
    distributors: [],
    unsubscribeFromDistributors: () => void {},
  },

  getters: {
    distributors: (state) => state.distributors,
  },

  mutations: {
    setDistributors(state, data: Distributor[]) {
      state.distributors = data
    },
    setUnsubscribeFromDistributors(state, data: () => void) {
      state.unsubscribeFromDistributors = data
    },
  },

  actions: {
    subscribeToDistributors: async ({ commit, dispatch }) => {
      try {
        commit('setLoading', null, { root: true })
        dispatch('unsubscribeFromDistributors')

        const distributorCollection = firebase.firestore().collection('distributors')

        const unsubsFunc = distributorCollection.onSnapshot(
          (snapshot) => {
            const distributors = snapshot.docs.map((doc) => ({
              id: doc.id,
              ...doc.data(),
            }))
            commit('setDistributors', distributors)
          },
          (error) => {
            commit('setError', error.message, { root: true })
          }
        )

        commit('setUnsubscribeFromDistributors', unsubsFunc)
      } catch (error: any) {
        commit('setError', error.message, { root: true })
      } finally {
        commit('setLoading', false, { root: true })
      }
    },

    unsubscribeFromDistributors: ({ state, commit }) => {
      state.unsubscribeFromDistributors()
      commit('setUnsubscribeFromDistributors', () => void {})
    },

    createDistributors: async ({ commit }, newDistributors: Distributor[]): Promise<string[]> => {
      try {
        commit('setLoading', true, { root: true })

        const distributorCollection = firebase.firestore().collection('distributors')
        const batch = firebase.firestore().batch()
        const newIds: string[] = []

        newDistributors.forEach((distributor) => {
          const docRef = distributorCollection.doc()
          batch.set(docRef, { ...distributor, id: docRef.id })
          newIds.push(docRef.id)
        })

        await batch.commit()
        commit('setSuccess', 'Distributor(s) successfully created!', { root: true })

        return newIds
      } catch (error: any) {
        commit('setError', error.message, { root: true })
        return []
      } finally {
        commit('setLoading', false, { root: true })
      }
    },

    updateDistributor: async ({ commit }, newDistributor: Distributor): Promise<void> => {
      try {
        commit('setLoading', true, { root: true })
        const distributorCollection = firebase.firestore().collection('distributors')
        await distributorCollection.doc(newDistributor.id).update(newDistributor)

        commit('setSuccess', 'Distributor successfully modified!', { root: true })
      } catch (error: any) {
        commit('setError', error.message, { root: true })
      } finally {
        commit('setLoading', false, { root: true })
      }
    },

    removeDistributor: async ({ commit }, distributorId: string): Promise<void> => {
      try {
        commit('setLoading', true, { root: true })
        const distributorCollection = firebase.firestore().collection('distributors')
        await distributorCollection.doc(distributorId).delete()

        commit('setSuccess', 'Distributor successfully deleted!', { root: true })
      } catch (error: any) {
        commit('setError', error.message, { root: true })
      } finally {
        commit('setLoading', false, { root: true })
      }
    },
  },
}

export default distributorsModule
